/**
 * ConfigMappingPage - Page de configuration des statuts intervention
 * Permet de mapper les codes Image vers les statuts Cloud
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useAuth, useHasPermission } from '../SupabaseWrapper';
import {
  RefreshIcon,
  CheckIcon,
  CloseIcon,
  WrenchIcon,
  ShieldIcon,
} from '../icons';
import { useViewport } from '../hooks/useViewport';
import { etatVerrou } from '../shared/verrousStatuts';
import ResponsiveCardList from '../components/responsive/ResponsiveCardList';

// Types
interface OretarConfig {
  crm_label: string;       // Libellé CRM (AS400)
  tech_code: string | null; // Code technique (pending, in_progress, etc.)
  tech_label: string | null; // Libellé technique
  color: string | null;
  auto_archive?: boolean;   // Si true, archive auto lors du sync
  locks_writeback?: boolean;             // Si true, l'app refuse la clôture d'un ordre dans cet état
  locks_writeback_message?: string | null; // Le texte lu par le technicien ; null = texte par défaut
  fromDB?: boolean;
  // Suggestions pour l'UI
  suggestedTechCode?: string | null;
  suggestedTechLabel?: string | null;
  suggestedColor?: string | null;
  // Alias pour compatibilité descendante
  label?: string;          // @deprecated → crm_label
  status?: string;         // @deprecated → tech_code
}

interface TechStatus {
  tech_code: string;
  tech_label: string;
  color: string;
  enabled?: boolean;
  description?: string;
  required?: boolean;      // Si true, pas de toggle (toujours actif)
  // Alias pour compatibilité
  value?: string;          // @deprecated → tech_code
  label?: string;          // @deprecated → tech_label
}

interface MappingData {
  codeDossier: string;
  availableStatuses: TechStatus[];
  codesFromDB: {
    oretar: Array<{ code: string; label: string }>;
    orcatl: Array<{ code: string; label: string }>;
    orcorg: Array<{ code: string; label: string }>;
  };
  currentConfig: {
    oretar: Record<string, OretarConfig>;
    orcatl: Record<string, { label: string }>;
    orcorg: Record<string, { label: string }>;
    updatedAt?: string;
    unmappedCodes?: string[];
    mappingComplete?: boolean;
  };
}

// URL API backend
const API_URL = '';

// Valeur spéciale pour "Ne pas synchroniser"
const IGNORE_STATUS_VALUE = '_ignore_';

// Props du composant
interface ConfigMappingPageProps {
  refreshTrigger?: number;
}

// Carte mobile pour un mapping ORETAR (utilisée sur < 768px)
function OretarMappingMobileCard({
  code,
  config,
  availableStatuses,
  onStatusChange,
  onAutoArchiveChange,
  onVerrouChange,
  onMessageRefusChange,
  readOnly,
}: {
  code: string;
  config: OretarConfig;
  availableStatuses: TechStatus[];
  onStatusChange: (techCode: string) => void;
  onAutoArchiveChange: (checked: boolean) => void;
  onVerrouChange: (checked: boolean) => void;
  onMessageRefusChange: (texte: string) => void;
  readOnly: boolean;
}) {
  const crmLabel = config.crm_label || config.label || `Statut ${code}`;
  const techCode = config.tech_code || config.status || '';
  const displayColor = config.color || '#6B7280';
  const isUnmapped = !techCode;
  const isIgnored = techCode === IGNORE_STATUS_VALUE;

  let cardBg = 'white';
  if (isUnmapped) cardBg = '#fef3c7';
  else if (isIgnored) cardBg = '#f3f4f6';

  return (
    <div style={{
      backgroundColor: cardBg,
      borderRadius: 12,
      padding: 16,
      boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
      display: 'flex',
      flexDirection: 'column',
      gap: 12,
    }}>
      {/* Header : code + libellé Image + indicateur état */}
      <div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4, flexWrap: 'wrap' }}>
          <strong style={{ fontSize: 16 }}>{code}</strong>
          {config.fromDB && (
            <span style={{ fontSize: 11, color: '#6b7280' }}>(Image)</span>
          )}
          {isUnmapped && (
            <span style={{ fontSize: 11, color: '#d97706' }}>⚠️ Non mappé</span>
          )}
          {isIgnored && (
            <span style={{ fontSize: 11, color: '#6b7280' }}>🚫 Ignoré</span>
          )}
        </div>
        <div style={{ fontSize: 14, color: '#475569' }}>{crmLabel}</div>
      </div>

      {/* Select statut application */}
      <div>
        <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#475569', marginBottom: 4 }}>
          Statut Application
        </label>
        <select
          value={techCode}
          onChange={(e) => onStatusChange(e.target.value)}
          disabled={readOnly}
          style={{
            width: '100%',
            padding: '10px 12px',
            borderRadius: 6,
            border: '1px solid ' + (isUnmapped ? '#f59e0b' : isIgnored ? '#9ca3af' : '#d1d5db'),
            backgroundColor: readOnly ? '#f3f4f6' : isUnmapped ? '#fffbeb' : isIgnored ? '#f9fafb' : 'white',
            fontSize: 14,
            cursor: readOnly ? 'not-allowed' : 'pointer',
          }}
        >
          {isUnmapped && <option value="">-- Sélectionner un statut --</option>}
          <option value={IGNORE_STATUS_VALUE}>🚫 Ne pas synchroniser</option>
          {availableStatuses
            .filter((s) => s.enabled !== false && s.tech_code !== 'paused')
            .map((s) => {
              const sCode = s.tech_code || s.value || '';
              const sLabel = s.tech_label || s.label || sCode;
              return <option key={sCode} value={sCode}>{sLabel} ({sCode})</option>;
            })}
        </select>
      </div>

      {/* Switch archive */}
      <label style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        cursor: (isIgnored || readOnly) ? 'not-allowed' : 'pointer',
        opacity: (isIgnored || readOnly) ? 0.5 : 1,
      }}>
        <input
          type="checkbox"
          checked={config.auto_archive || false}
          disabled={isIgnored || readOnly}
          onChange={(e) => onAutoArchiveChange(e.target.checked)}
          style={{ width: 16, height: 16, cursor: (isIgnored || readOnly) ? 'not-allowed' : 'pointer' }}
        />
        <span style={{ fontSize: 14, color: '#475569' }}>Archiver automatiquement</span>
      </label>

      {/* Verrou de clôture (décision du 18/09/2026) : l'app refuse la clôture d'un ordre
          dans cet état, AVANT que le technicien fasse signer. 90 et 99 sont figés : c'est
          l'agent qui les refuse, sur la machine — la case ne ferait que mentir. */}
      {(() => {
        const v = etatVerrou(code, {
          locks_writeback: config.locks_writeback,
          locks_writeback_message: config.locks_writeback_message,
          crm_label: crmLabel,
        }, { ignore: isIgnored });
        return (
          <div style={{ borderTop: '1px solid #e5e7eb', paddingTop: 10 }}>
            <label style={{
              display: 'flex', alignItems: 'center', gap: 8,
              cursor: (v.figee || readOnly) ? 'not-allowed' : 'pointer',
              opacity: (v.figee && v.porte !== 'machine') || readOnly ? 0.5 : 1,
            }}>
              <input
                type="checkbox"
                checked={v.coche}
                disabled={v.figee || readOnly}
                onChange={(e) => onVerrouChange(e.target.checked)}
                style={{ width: 16, height: 16, cursor: (v.figee || readOnly) ? 'not-allowed' : 'pointer' }}
              />
              <span style={{ fontSize: 14, color: '#475569' }}>
                Interdit la clôture depuis le mobile
                {v.porte === 'machine' && (
                  <span style={{ fontSize: 11, color: '#b45309', marginLeft: 6 }}>verrou machine</span>
                )}
              </span>
            </label>
            <div style={{ fontSize: 11, color: '#6b7280', marginTop: 4 }}>{v.aide}</div>
            {v.coche && (
              <input
                type="text"
                value={config.locks_writeback_message ?? ''}
                placeholder={v.apercu}
                disabled={readOnly}
                onChange={(e) => onMessageRefusChange(e.target.value)}
                style={{
                  width: '100%', marginTop: 8, padding: '8px 10px', borderRadius: 6,
                  border: '1px solid #d1d5db', fontSize: 13,
                  backgroundColor: readOnly ? '#f3f4f6' : 'white',
                }}
              />
            )}
          </div>
        );
      })()}

      {/* Aperçu badge */}
      <div>
        {isIgnored ? (
          <span style={{
            display: 'inline-flex',
            padding: '4px 10px',
            borderRadius: 9999,
            fontSize: 12,
            backgroundColor: '#f3f4f6',
            color: '#6b7280',
            fontStyle: 'italic',
          }}>
            🚫 Non synchronisé
          </span>
        ) : (
          <span style={{
            display: 'inline-flex',
            padding: '4px 10px',
            borderRadius: 9999,
            fontSize: 12,
            backgroundColor: `${displayColor}20`,
            color: displayColor,
          }}>
            {crmLabel}
          </span>
        )}
      </div>
    </div>
  );
}

function ConfigMappingPage({ refreshTrigger = 0 }: ConfigMappingPageProps) {
  const { user: _currentUser } = useAuth();
  // RBAC : permissions
  const canView = useHasPermission('config.view');
  const canEdit = useHasPermission('config.statuses.update');
  const canResetFromIbmi = useHasPermission('config.statuses.reset_from_ibmi');
  const canInit = useHasPermission('config.statuses.init');
  const { isPhone } = useViewport();

  const [mappingData, setMappingData] = useState<MappingData | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  // Mapping modifié (état local avant sauvegarde)
  const [editedMapping, setEditedMapping] = useState<Record<string, OretarConfig>>({});
  const [hasChanges, setHasChanges] = useState(false);

  // Charger les données de mapping
  const fetchMappingData = useCallback(async () => {
    console.log('[ConfigPage] Chargement du mapping...');
    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem('sso_token');
      console.log('[API] GET /api/config/mapping');

      const response = await fetch(`${API_URL}/api/config/mapping`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      console.log('[API] Response status:', response.status);

      if (!response.ok) {
        throw new Error(`Erreur ${response.status}: ${response.statusText}`);
      }

      const result = await response.json();
      console.log('[ConfigPage] Résultat reçu:', result.success, result.data?.codeDossier);

      if (result.success && result.data) {
        console.log('[ConfigPage] Codes statuts:', Object.keys(result.data.currentConfig.oretar || {}).length);
        setMappingData(result.data);
        setEditedMapping(result.data.currentConfig.oretar || {});
        setHasChanges(false);
      } else {
        throw new Error(result.error?.message || 'Erreur inconnue');
      }
    } catch (err) {
      console.error('[ConfigPage] Erreur:', err);
      setError(err instanceof Error ? err.message : 'Erreur de chargement');
    } finally {
      setLoading(false);
    }
  }, []);

  // Charger au montage
  useEffect(() => {
    fetchMappingData();
  }, [fetchMappingData]);

  // Refetch quand refreshTrigger change (navigation vers cet onglet)
  const isFirstMount = React.useRef(true);
  useEffect(() => {
    if (isFirstMount.current) {
      isFirstMount.current = false;
      return;
    }
    console.log('[ConfigPage] refreshTrigger changed, refetching...');
    fetchMappingData();
  }, [refreshTrigger, fetchMappingData]);

  // Réinitialiser depuis Image (ERP)
  const handleInitFromDB = async () => {
    console.log('[ConfigPage] Initialisation depuis Image (ERP)...');
    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem('sso_token');
      console.log('[API] POST /api/config/mapping/init');

      const response = await fetch(`${API_URL}/api/config/mapping/init`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const result = await response.json();
      console.log('[API] Response:', response.status, result.success);

      if (result.success) {
        console.log('[ConfigPage] Init OK, rechargement...');
        setSuccessMessage('Configuration initialisée depuis Image');
        await fetchMappingData();
        setTimeout(() => setSuccessMessage(null), 3000);
      } else {
        throw new Error(result.error?.message || 'Erreur initialisation');
      }
    } catch (err) {
      console.error('[ConfigPage] Erreur init:', err);
      setError(err instanceof Error ? err.message : 'Erreur');
    } finally {
      setLoading(false);
    }
  };

  // Sauvegarder les modifications
  const handleSave = async () => {
    console.log('[ConfigPage] Sauvegarde du mapping...');
    console.log('[ConfigPage] Codes à sauvegarder:', Object.keys(editedMapping).length);
    setSaving(true);
    setError(null);

    try {
      const token = localStorage.getItem('sso_token');
      console.log('[API] PUT /api/config/mapping/oretar');

      const response = await fetch(`${API_URL}/api/config/mapping/oretar`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ mapping: editedMapping })
      });

      const result = await response.json();
      console.log('[API] Response:', response.status, result.success);

      if (result.success) {
        console.log('[ConfigPage] Sauvegarde OK');

        // Sauvegarder aussi la config tech (labels + couleurs modifiés)
        if (mappingData) {
          await saveAppStatusConfig(mappingData.availableStatuses);
        }

        // Toast detaille : nombre d'interventions reclassees par le trigger
        // (la route renvoie propagation: { rowsAffected, breakdown })
        const rowsAffected = result.propagation?.rowsAffected || 0;
        const breakdown = result.propagation?.breakdown || [];
        let message = 'Mapping sauvegardé.';
        if (rowsAffected === 1) {
          message = 'Mapping sauvegardé. 1 intervention existante reclassée.';
        } else if (rowsAffected > 1) {
          message = `Mapping sauvegardé. ${rowsAffected} interventions existantes reclassées.`;
        }
        if (breakdown.length > 0) {
          console.log('[ConfigPage] Reclassement par code :', breakdown);
        }

        setSuccessMessage(message);
        setHasChanges(false);
        await fetchMappingData();
        setTimeout(() => setSuccessMessage(null), 5000);
      } else {
        throw new Error(result.error?.message || 'Erreur sauvegarde');
      }
    } catch (err) {
      console.error('[ConfigPage] Erreur sauvegarde:', err);
      setError(err instanceof Error ? err.message : 'Erreur');
    } finally {
      setSaving(false);
    }
  };

  // Modifier le statut d'un code
  const handleStatusChange = (code: string, newTechCode: string) => {
    console.log('[ConfigPage] Changement tech_code:', code, '->', newTechCode);

    // Cas spécial: "Ne pas synchroniser"
    if (newTechCode === IGNORE_STATUS_VALUE) {
      setEditedMapping(prev => ({
        ...prev,
        [code]: {
          ...prev[code],
          tech_code: IGNORE_STATUS_VALUE,
          tech_label: 'Ne pas synchroniser',
          color: '#9CA3AF' // Gris neutre
        }
      }));
      setHasChanges(true);
      return;
    }

    const techStatus = mappingData?.availableStatuses.find(
      s => (s.tech_code || s.value) === newTechCode
    );

    setEditedMapping(prev => ({
      ...prev,
      [code]: {
        ...prev[code],
        tech_code: newTechCode,
        tech_label: techStatus?.tech_label || techStatus?.label || newTechCode,
        // Couleur héritée du statut technique
        color: techStatus?.color || '#6B7280'
      }
    }));
    setHasChanges(true);
  };

  // Modifier la couleur d'un statut technique (propage à tous les codes mappés)
  const handleTechColorChange = (techCode: string, newColor: string) => {
    if (!mappingData) return;
    console.log('[ConfigPage] Changement couleur tech:', techCode, '->', newColor);

    // 1. Mettre à jour availableStatuses local
    const updatedStatuses = mappingData.availableStatuses.map(s =>
      (s.tech_code || s.value) === techCode ? { ...s, color: newColor } : s
    );
    setMappingData(prev => prev ? { ...prev, availableStatuses: updatedStatuses } : prev);

    // 2. Propager aux codes ORETAR mappés vers ce techCode
    setEditedMapping(prev => {
      const updated = { ...prev };
      for (const [code, config] of Object.entries(updated)) {
        if ((config.tech_code || config.status) === techCode) {
          updated[code] = { ...config, color: newColor };
        }
      }
      return updated;
    });
    setHasChanges(true);
  };

  // Modifier le libellé d'un statut technique
  const handleTechLabelChange = (techCode: string, newLabel: string) => {
    if (!mappingData) return;

    // Mettre à jour availableStatuses local
    const updatedStatuses = mappingData.availableStatuses.map(s =>
      (s.tech_code || s.value) === techCode ? { ...s, tech_label: newLabel, label: newLabel } : s
    );
    setMappingData(prev => prev ? { ...prev, availableStatuses: updatedStatuses } : prev);
    setHasChanges(true);
  };

  // Annuler les modifications
  const handleCancel = () => {
    console.log('[ConfigPage] Annulation des modifications');
    if (mappingData) {
      setEditedMapping(mappingData.currentConfig.oretar || {});
      setHasChanges(false);
    }
  };

  // Toggle un statut technique (activer/désactiver)
  const [togglingStatus, setTogglingStatus] = useState(false);

  const handleToggleAppStatus = async (techCode: string, enabled: boolean) => {
    if (!mappingData) return;
    setTogglingStatus(true);
    setError(null);

    try {
      // Mettre à jour la liste locale
      const updatedStatuses = mappingData.availableStatuses.map(s =>
        (s.tech_code || s.value) === techCode ? { ...s, enabled } : s
      );

      await saveAppStatusConfig(updatedStatuses);

      // Mettre à jour l'état local
      setMappingData(prev => prev ? { ...prev, availableStatuses: updatedStatuses } : prev);
      console.log(`[ConfigPage] Statut ${techCode} ${enabled ? 'activé' : 'désactivé'}`);
    } catch (err) {
      console.error('[ConfigPage] Erreur toggle statut:', err);
      setError(err instanceof Error ? err.message : 'Erreur lors de la mise à jour');
    } finally {
      setTogglingStatus(false);
    }
  };

  // Sauvegarder la config app_status (labels, couleurs, toggles)
  const saveAppStatusConfig = async (statuses: TechStatus[]) => {
    const config = statuses.map(s => ({
      key: s.tech_code || s.value || '',
      label: s.tech_label || s.label || '',
      color: s.color,
      enabled: s.required ? true : (s.enabled !== false),
      description: s.description || '',
      required: s.required || false
    }));

    const token = localStorage.getItem('sso_token');
    const response = await fetch(`${API_URL}/api/config/app-statuses`, {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ config })
    });

    const result = await response.json();
    if (!result.success) {
      throw new Error(result.error?.message || 'Erreur inconnue');
    }
  };


  // Styles
  const styles = {
    container: {
      padding: '24px',
      maxWidth: '1200px',
      margin: '0 auto'
    } as React.CSSProperties,
    header: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '24px',
      flexWrap: 'wrap' as const,
      gap: '16px'
    } as React.CSSProperties,
    title: {
      fontSize: '24px',
      fontWeight: '600',
      color: '#1f2937',
      display: 'flex',
      alignItems: 'center',
      gap: '12px'
    } as React.CSSProperties,
    actions: {
      display: 'flex',
      gap: '12px',
      flexWrap: 'wrap' as const
    } as React.CSSProperties,
    button: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      padding: '10px 16px',
      borderRadius: '8px',
      border: 'none',
      cursor: 'pointer',
      fontSize: '14px',
      fontWeight: '500',
      transition: 'all 0.15s'
    } as React.CSSProperties,
    primaryButton: {
      backgroundColor: '#3b82f6',
      color: 'white'
    } as React.CSSProperties,
    secondaryButton: {
      backgroundColor: '#e5e7eb',
      color: '#374151'
    } as React.CSSProperties,
    successButton: {
      backgroundColor: '#22c55e',
      color: 'white'
    } as React.CSSProperties,
    dangerButton: {
      backgroundColor: '#ef4444',
      color: 'white'
    } as React.CSSProperties,
    disabledButton: {
      opacity: 0.5,
      cursor: 'not-allowed'
    } as React.CSSProperties,
    card: {
      backgroundColor: 'white',
      borderRadius: '12px',
      boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
      padding: '24px',
      marginBottom: '24px'
    } as React.CSSProperties,
    cardTitle: {
      fontSize: '18px',
      fontWeight: '600',
      color: '#1f2937',
      marginBottom: '16px'
    } as React.CSSProperties,
    table: {
      width: '100%',
      borderCollapse: 'collapse' as const
    } as React.CSSProperties,
    th: {
      textAlign: 'left' as const,
      padding: '12px',
      borderBottom: '2px solid #e5e7eb',
      fontSize: '13px',
      fontWeight: '600',
      color: '#6b7280',
      textTransform: 'uppercase' as const
    } as React.CSSProperties,
    td: {
      padding: '12px',
      borderBottom: '1px solid #f3f4f6',
      fontSize: '14px',
      color: '#374151'
    } as React.CSSProperties,
    select: {
      padding: '8px 12px',
      borderRadius: '6px',
      border: '1px solid #d1d5db',
      fontSize: '14px',
      minWidth: '160px',
      cursor: 'pointer'
    } as React.CSSProperties,
    colorInput: {
      width: '36px',
      height: '32px',
      padding: '2px',
      borderRadius: '6px 0 0 6px',
      border: '1px solid #d1d5db',
      borderRight: 'none',
      cursor: 'pointer'
    } as React.CSSProperties,
    colorHexInput: {
      width: '75px',
      height: '32px',
      padding: '4px 8px',
      borderRadius: '0 6px 6px 0',
      border: '1px solid #d1d5db',
      fontSize: '12px',
      fontFamily: 'monospace',
      textTransform: 'uppercase'
    } as React.CSSProperties,
    badge: {
      display: 'inline-flex',
      alignItems: 'center',
      padding: '4px 10px',
      borderRadius: '9999px',
      fontSize: '12px',
      fontWeight: '500'
    } as React.CSSProperties,
    alert: {
      padding: '12px 16px',
      borderRadius: '8px',
      marginBottom: '16px',
      display: 'flex',
      alignItems: 'center',
      gap: '8px'
    } as React.CSSProperties,
    alertError: {
      backgroundColor: '#fef2f2',
      color: '#dc2626',
      border: '1px solid #fecaca'
    } as React.CSSProperties,
    alertSuccess: {
      backgroundColor: '#f0fdf4',
      color: '#16a34a',
      border: '1px solid #bbf7d0'
    } as React.CSSProperties,
    infoBox: {
      backgroundColor: '#f0f9ff',
      border: '1px solid #bae6fd',
      borderRadius: '8px',
      padding: '16px',
      marginBottom: '24px'
    } as React.CSSProperties,
    infoText: {
      color: '#0369a1',
      fontSize: '14px',
      margin: 0
    } as React.CSSProperties,
    loader: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      padding: '48px'
    } as React.CSSProperties,
    spinner: {
      width: '40px',
      height: '40px',
      border: '3px solid #e5e7eb',
      borderTopColor: '#3b82f6',
      borderRadius: '50%',
      animation: 'spin 0.8s linear infinite'
    } as React.CSSProperties
  };

  // Gate d'entree : permission config.view
  if (!canView) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '60vh', gap: 16 }}>
        <ShieldIcon size={64} color="#ef4444" />
        <h2 style={{ color: '#1e293b', margin: 0 }}>Accès refusé</h2>
        <p style={{ color: '#64748b', margin: 0 }}>Vous n'avez pas les autorisations nécessaires pour accéder à cette page.</p>
      </div>
    );
  }

  // Affichage chargement
  if (loading && !mappingData) {
    return (
      <div style={{ ...styles.container, padding: isPhone ? 12 : 24 }}>
        <div style={styles.loader}>
          <div style={styles.spinner} />
        </div>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  // Tri des codes statut
  const sortedCodes = Object.entries(editedMapping).sort(([a], [b]) => a.localeCompare(b));

  // Détection en temps réel des libellés dupliqués entre statuts techniques.
  // Le backend (PUT /api/config/app-statuses) rejette ce cas en 400 DUPLICATE_LABEL :
  // on bloque la sauvegarde côté UI pour donner un feedback immédiat à l'admin.
  const labelGroups = new Map<string, { displayLabel: string; codes: string[] }>();
  mappingData?.availableStatuses.forEach(s => {
    const rawLabel = (s.tech_label || s.label || '').trim();
    if (!rawLabel) return;
    const key = rawLabel.toLowerCase();
    const techCode = s.tech_code || s.value || '';
    const existing = labelGroups.get(key);
    if (existing) {
      existing.codes.push(techCode);
    } else {
      labelGroups.set(key, { displayLabel: rawLabel, codes: [techCode] });
    }
  });
  const duplicateLabels = Array.from(labelGroups.values()).filter(g => g.codes.length > 1);
  const hasDuplicateLabels = duplicateLabels.length > 0;

  return (
    <div style={{ ...styles.container, padding: isPhone ? 12 : 24 }}>
      {/* Header */}
      <div style={styles.header}>
        <h1 style={styles.title}>
          <WrenchIcon size={28} color="#3b82f6" />
          Statuts intervention
        </h1>
        <div style={styles.actions}>
          <button
            style={{
              ...styles.button,
              ...styles.secondaryButton,
              ...(loading ? styles.disabledButton : {})
            }}
            onClick={fetchMappingData}
            disabled={loading}
            title="Actualiser les données"
          >
            <RefreshIcon size={18} />
            Actualiser
          </button>
          {(canInit || canResetFromIbmi) && (
            <button
              style={{
                ...styles.button,
                ...styles.secondaryButton,
                ...(loading ? styles.disabledButton : {})
              }}
              onClick={handleInitFromDB}
              disabled={loading}
            >
              Réinitialiser depuis Image
            </button>
          )}
          {canEdit && hasChanges && (
            <>
              <button
                style={{
                  ...styles.button,
                  ...styles.dangerButton
                }}
                onClick={handleCancel}
              >
                <CloseIcon size={18} />
                Annuler
              </button>
              <button
                style={{
                  ...styles.button,
                  ...styles.successButton,
                  ...(saving || hasDuplicateLabels ? styles.disabledButton : {})
                }}
                onClick={handleSave}
                disabled={saving || hasDuplicateLabels}
                title={hasDuplicateLabels ? 'Conflit de libellés à résoudre avant de sauvegarder' : undefined}
              >
                <CheckIcon size={18} />
                {saving ? 'Sauvegarde...' : 'Sauvegarder'}
              </button>
            </>
          )}
        </div>
      </div>

      {/* Messages */}
      {error && (
        <div style={{ ...styles.alert, ...styles.alertError }}>
          <CloseIcon size={18} />
          {error}
        </div>
      )}

      {successMessage && (
        <div style={{ ...styles.alert, ...styles.alertSuccess }}>
          <CheckIcon size={18} />
          {successMessage}
        </div>
      )}

      {/* Info box */}
      <div style={styles.infoBox}>
        <p style={styles.infoText}>
          <strong>Dossier:</strong> {mappingData?.codeDossier || 'N/A'}
          {mappingData?.currentConfig.updatedAt && (
            <> | <strong>Dernière mise à jour:</strong> {new Date(mappingData.currentConfig.updatedAt).toLocaleString()}</>
          )}
        </p>
        <p style={{ ...styles.infoText, marginTop: '8px' }}>
          Ce tableau définit comment les codes statut d'Image sont traduits en statuts pour l'application mobile.
        </p>
      </div>

      {/* Bandeau d'erreur : libellés dupliqués (bloque la sauvegarde) */}
      {hasDuplicateLabels && (
        <div style={{
          padding: '16px',
          backgroundColor: '#fef2f2',
          border: '1px solid #ef4444',
          borderRadius: '8px',
          marginBottom: '16px'
        }}>
          <div style={{
            fontWeight: 600,
            color: '#991b1b',
            marginBottom: '8px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#991b1b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10"/>
              <line x1="15" y1="9" x2="9" y2="15"/>
              <line x1="9" y1="9" x2="15" y2="15"/>
            </svg>
            Conflit de libellés détecté
          </div>
          <ul style={{ margin: 0, paddingLeft: '24px', color: '#991b1b', fontSize: '13px' }}>
            {duplicateLabels.map(({ displayLabel, codes }) => (
              <li key={displayLabel} style={{ marginBottom: '4px' }}>
                Le libellé «&nbsp;<strong>{displayLabel}</strong>&nbsp;» est utilisé par plusieurs statuts ({codes.join(', ')}). Chaque statut technique doit avoir un libellé unique.
              </li>
            ))}
          </ul>
          <p style={{ margin: '8px 0 0 0', color: '#991b1b', fontSize: '13px', fontStyle: 'italic' }}>
            La sauvegarde est désactivée tant que ce conflit n'est pas résolu.
          </p>
        </div>
      )}

      {/* Avertissements mapping */}
      {editedMapping && (() => {
        const inProgressCount = Object.values(editedMapping).filter(
          (c: any) => (c.tech_code || c.status) === 'in_progress'
        ).length;
        const hasCompleted = Object.values(editedMapping).some(
          (c: any) => (c.tech_code || c.status) === 'completed'
        );
        const hasCancelled = Object.values(editedMapping).some(
          (c: any) => (c.tech_code || c.status) === 'cancelled'
        );
        const warnings: string[] = [];
        if (inProgressCount === 0) warnings.push('Aucun statut "En cours" configuré — le technicien ne pourra pas démarrer d\'intervention.');
        if (inProgressCount > 1) warnings.push(`${inProgressCount} statuts "En cours" configurés — un seul est autorisé pour le démarrage automatique.`);
        if (!hasCompleted) warnings.push('Aucun statut "Terminé" configuré — le technicien ne pourra pas clôturer d\'intervention.');
        if (!hasCancelled) warnings.push('Aucun statut "Annulé" configuré — le technicien ne pourra pas annuler d\'intervention.');
        if (warnings.length === 0) return null;
        return (
          <div style={{
            padding: '16px',
            backgroundColor: '#fef3c7',
            border: '1px solid #f59e0b',
            borderRadius: '8px',
            marginBottom: '16px'
          }}>
            <div style={{ fontWeight: '600', color: '#92400e', marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#92400e" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
              Configuration incomplète
            </div>
            <ul style={{ margin: 0, paddingLeft: '24px', color: '#92400e', fontSize: '13px' }}>
              {warnings.map((w, i) => <li key={i} style={{ marginBottom: '4px' }}>{w}</li>)}
            </ul>
          </div>
        );
      })()}

      {/* Tableau de mapping */}
      <div style={styles.card}>
        <h2 style={styles.cardTitle}>Codes statut → Statuts application</h2>

        <ResponsiveCardList
          items={sortedCodes}
          renderCard={([code, config]) => (
            <OretarMappingMobileCard
              key={code}
              code={code}
              config={config}
              availableStatuses={mappingData?.availableStatuses || []}
              readOnly={!canEdit}
              onStatusChange={(techCode) => handleStatusChange(code, techCode)}
              onAutoArchiveChange={(checked) => {
                setEditedMapping(prev => ({
                  ...prev,
                  [code]: { ...prev[code], auto_archive: checked }
                }));
                setHasChanges(true);
              }}
              onVerrouChange={(checked) => {
                setEditedMapping(prev => ({
                  ...prev,
                  // Décocher efface le message : le garder inviterait à le retrouver
                  // affiché un jour sans avoir été relu.
                  [code]: { ...prev[code], locks_writeback: checked,
                            locks_writeback_message: checked ? prev[code]?.locks_writeback_message ?? null : null }
                }));
                setHasChanges(true);
              }}
              onMessageRefusChange={(texte) => {
                setEditedMapping(prev => ({
                  ...prev,
                  [code]: { ...prev[code], locks_writeback_message: texte }
                }));
                setHasChanges(true);
              }}
            />
          )}
          fallback={
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>Code</th>
                  <th style={styles.th}>Libellé Image</th>
                  <th style={styles.th}>Statut Application</th>
                  <th style={{ ...styles.th, textAlign: 'center' }}>Archive ?</th>
                  <th style={{ ...styles.th, textAlign: 'center' }}>Interdit la clôture ?</th>
                  <th style={styles.th}>Aperçu</th>
                </tr>
              </thead>
              <tbody>
                {sortedCodes.map(([code, config]) => {
                  // Compatibilité: utiliser les nouveaux champs ou les anciens
                  const crmLabel = config.crm_label || config.label || `Statut ${code}`;
                  const techCode = config.tech_code || config.status || '';
                  const displayColor = config.color || '#6B7280';
                  const isUnmapped = !techCode;
                  const isIgnored = techCode === IGNORE_STATUS_VALUE;

                  // Déterminer le style de la ligne
                  let rowStyle: React.CSSProperties = {};
                  if (isUnmapped) {
                    rowStyle = { backgroundColor: '#fef3c7' }; // Jaune = non mappé (warning)
                  } else if (isIgnored) {
                    rowStyle = { backgroundColor: '#f3f4f6' }; // Gris clair = ignoré (neutre)
                  }

                  return (
                    <tr key={code} style={rowStyle}>
                      <td style={styles.td}>
                        <strong>{code}</strong>
                        {config.fromDB && (
                          <span style={{ fontSize: '11px', color: '#6b7280', marginLeft: '8px' }}>
                            (Image)
                          </span>
                        )}
                        {isUnmapped && (
                          <span style={{ fontSize: '11px', color: '#d97706', marginLeft: '8px' }}>
                            ⚠️ Non mappé
                          </span>
                        )}
                        {isIgnored && (
                          <span style={{ fontSize: '11px', color: '#6b7280', marginLeft: '8px' }}>
                            🚫 Ignoré
                          </span>
                        )}
                      </td>
                      <td style={styles.td}>{crmLabel}</td>
                      <td style={styles.td}>
                        <select
                          style={{
                            ...styles.select,
                            ...(isUnmapped ? { borderColor: '#f59e0b', backgroundColor: '#fffbeb' } : {}),
                            ...(isIgnored ? { borderColor: '#9ca3af', backgroundColor: '#f9fafb' } : {}),
                            ...(!canEdit ? { backgroundColor: '#f3f4f6', cursor: 'not-allowed' } : {})
                          }}
                          value={techCode}
                          disabled={!canEdit}
                          onChange={(e) => handleStatusChange(code, e.target.value)}
                        >
                          {isUnmapped && (
                            <option value="">-- Sélectionner un statut --</option>
                          )}
                          <option value={IGNORE_STATUS_VALUE}>🚫 Ne pas synchroniser</option>
                          {mappingData?.availableStatuses
                            .filter((status) => status.enabled !== false && status.tech_code !== 'paused')
                            .map((status) => {
                              const statusCode = status.tech_code || status.value || '';
                              const statusLabel = status.tech_label || status.label || statusCode;
                              return (
                                <option key={statusCode} value={statusCode}>
                                  {statusLabel} ({statusCode})
                                </option>
                              );
                            })}
                        </select>
                      </td>
                      <td style={{ ...styles.td, textAlign: 'center' }}>
                        <input
                          type="checkbox"
                          checked={config.auto_archive || false}
                          disabled={isIgnored || !canEdit}
                          onChange={(e) => {
                            setEditedMapping(prev => ({
                              ...prev,
                              [code]: {
                                ...prev[code],
                                auto_archive: e.target.checked
                              }
                            }));
                            setHasChanges(true);
                          }}
                          title={isIgnored ? 'Non disponible pour les statuts ignorés' : !canEdit ? 'Lecture seule' : 'Archiver automatiquement les interventions avec ce statut'}
                          style={{ cursor: (isIgnored || !canEdit) ? 'not-allowed' : 'pointer', width: '16px', height: '16px' }}
                        />
                      </td>
                      {/* Verrou de clôture + le message lu par le technicien (18/09/2026) */}
                      <td style={{ ...styles.td, textAlign: 'center', minWidth: 220 }}>
                        {(() => {
                          const v = etatVerrou(code, {
                            locks_writeback: config.locks_writeback,
                            locks_writeback_message: config.locks_writeback_message,
                            crm_label: crmLabel,
                          }, { ignore: isIgnored });
                          return (
                            <>
                              <input
                                type="checkbox"
                                checked={v.coche}
                                disabled={v.figee || !canEdit}
                                onChange={(e) => {
                                  const checked = e.target.checked;
                                  setEditedMapping(prev => ({
                                    ...prev,
                                    [code]: {
                                      ...prev[code],
                                      locks_writeback: checked,
                                      locks_writeback_message: checked
                                        ? prev[code]?.locks_writeback_message ?? null
                                        : null
                                    }
                                  }));
                                  setHasChanges(true);
                                }}
                                title={v.aide}
                                style={{ cursor: (v.figee || !canEdit) ? 'not-allowed' : 'pointer', width: '16px', height: '16px' }}
                              />
                              {v.porte === 'machine' && (
                                <div style={{ fontSize: 10, color: '#b45309', marginTop: 2 }}>verrou machine</div>
                              )}
                              {v.coche && (
                                <input
                                  type="text"
                                  value={config.locks_writeback_message ?? ''}
                                  placeholder={v.apercu}
                                  disabled={!canEdit}
                                  onChange={(e) => {
                                    setEditedMapping(prev => ({
                                      ...prev,
                                      [code]: { ...prev[code], locks_writeback_message: e.target.value }
                                    }));
                                    setHasChanges(true);
                                  }}
                                  title="Message lu par le technicien. Vide = texte par défaut (affiché en gris)."
                                  style={{
                                    width: '100%', marginTop: 6, padding: '6px 8px', borderRadius: 6,
                                    border: '1px solid #d1d5db', fontSize: 12,
                                    backgroundColor: canEdit ? 'white' : '#f3f4f6',
                                  }}
                                />
                              )}
                            </>
                          );
                        })()}
                      </td>
                      <td style={styles.td}>
                        {isIgnored ? (
                          <span
                            style={{
                              ...styles.badge,
                              backgroundColor: '#f3f4f6',
                              color: '#6b7280',
                              fontStyle: 'italic'
                            }}
                          >
                            🚫 Non synchronisé
                          </span>
                        ) : (
                          <span
                            style={{
                              ...styles.badge,
                              backgroundColor: `${displayColor}20`,
                              color: displayColor
                            }}
                          >
                            {crmLabel}
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          }
          emptyMessage="Aucun statut configuré. Cliquez sur « Réinitialiser depuis Image » pour charger les codes."
        />

        {sortedCodes.length === 0 && !isPhone && (
          <p style={{ textAlign: 'center', color: '#6b7280', padding: '24px' }}>
            Aucun statut configuré. Cliquez sur « Réinitialiser depuis Image » pour charger les codes.
          </p>
        )}
      </div>

      {/* Statuts techniques disponibles — avec toggles */}
      <div style={styles.card}>
        <h2 style={styles.cardTitle}>Statuts techniques disponibles</h2>
        <p style={{ color: '#6b7280', fontSize: '13px', margin: '0 0 12px 0' }}>
          Activez ou désactivez les catégories de statuts utilisées par votre établissement.
        </p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {mappingData?.availableStatuses.map((status) => {
            const techCode = status.tech_code || status.value || '';
            const techLabel = status.tech_label || status.label || techCode;
            const isEnabled = status.enabled !== false;
            const isRequired = status.required === true;
            return (
              <div
                key={techCode}
                style={{
                  display: 'flex',
                  flexDirection: isPhone ? 'column' : 'row',
                  alignItems: isPhone ? 'stretch' : 'center',
                  gap: isPhone ? '8px' : '12px',
                  padding: '10px 12px',
                  borderRadius: '8px',
                  backgroundColor: isEnabled ? `${status.color}10` : '#f3f4f6',
                  border: `1px solid ${isEnabled ? status.color + '30' : '#e5e7eb'}`,
                  opacity: isEnabled ? 1 : 0.6,
                  transition: 'all 0.2s ease'
                }}
              >
                {/* Header row sur phone (toggle + color + techCode badge),
                    elements en ligne sur desktop */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  {/* Toggle switch (hidden if required) */}
                  {isRequired ? (
                    <span style={{
                      width: '40px', height: '22px', flexShrink: 0,
                      display: 'flex', alignItems: 'center', justifyContent: 'center'
                    }}>
                      <span style={{ fontSize: '11px', color: '#9ca3af', fontStyle: 'italic' }}>requis</span>
                    </span>
                  ) : (
                    <label style={{ position: 'relative', display: 'inline-block', width: '40px', height: '22px', flexShrink: 0 }}>
                      <input
                        type="checkbox"
                        checked={isEnabled}
                        onChange={(e) => handleToggleAppStatus(techCode, e.target.checked)}
                        disabled={togglingStatus || !canEdit}
                        style={{ opacity: 0, width: 0, height: 0 }}
                      />
                      <span style={{
                        position: 'absolute', cursor: (togglingStatus || !canEdit) ? 'not-allowed' : 'pointer',
                        top: 0, left: 0, right: 0, bottom: 0,
                        backgroundColor: isEnabled ? status.color : '#d1d5db',
                        borderRadius: '22px', transition: 'background-color 0.2s'
                      }}>
                        <span style={{
                          position: 'absolute', height: '16px', width: '16px',
                          left: isEnabled ? '21px' : '3px', bottom: '3px',
                          backgroundColor: '#fff', borderRadius: '50%',
                          transition: 'left 0.2s'
                        }} />
                      </span>
                    </label>
                  )}
                  {/* Color picker */}
                  <input
                    type="color"
                    value={status.color || '#6B7280'}
                    onChange={(e) => handleTechColorChange(techCode, e.target.value)}
                    disabled={!canEdit}
                    style={{
                      width: '28px', height: '28px', padding: '1px', borderRadius: '6px',
                      border: '1px solid #d1d5db', cursor: canEdit ? 'pointer' : 'not-allowed',
                      flexShrink: 0
                    }}
                  />
                  {/* Sur phone : techCode bascule a droite de la header row */}
                  {isPhone && (
                    <span style={{
                      marginLeft: 'auto',
                      fontSize: '12px', color: '#9ca3af',
                      fontFamily: 'monospace', flexShrink: 0
                    }}>
                      {techCode}
                    </span>
                  )}
                </div>
                {/* Editable label (max 12 chars) + description */}
                <div style={{
                  display: 'flex', flexDirection: 'column', gap: '2px', minWidth: 0,
                  flex: isPhone ? undefined : 1
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <input
                      type="text"
                      value={techLabel}
                      onChange={(e) => {
                        if (e.target.value.length <= 12) {
                          handleTechLabelChange(techCode, e.target.value);
                        }
                      }}
                      maxLength={12}
                      disabled={(!isEnabled && !isRequired) || !canEdit}
                      style={{
                        width: isPhone ? '100%' : '120px',
                        flex: isPhone ? 1 : undefined,
                        padding: '4px 8px', borderRadius: '6px',
                        border: `1px solid ${isEnabled ? '#d1d5db' : '#e5e7eb'}`,
                        fontSize: '14px', fontWeight: 500,
                        color: isEnabled ? '#111827' : '#9ca3af',
                        backgroundColor: isEnabled ? '#fff' : '#f9fafb',
                        textDecoration: (isEnabled || isRequired) ? 'none' : 'line-through',
                        boxSizing: 'border-box'
                      }}
                    />
                    {/* Sur desktop : techCode reste a cote de l'input */}
                    {!isPhone && (
                      <span style={{
                        fontSize: '12px', color: '#9ca3af',
                        fontFamily: 'monospace', flexShrink: 0
                      }}>
                        {techCode}
                      </span>
                    )}
                  </div>
                  {status.description && (
                    <span style={{
                      fontSize: '12px', color: '#9ca3af',
                      paddingLeft: isPhone ? 0 : '8px'
                    }}>
                      {status.description}
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Styles pour spinner */}
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

export default ConfigMappingPage;
